import { useCallback, useEffect, useState } from 'react';
import type { Account, Entitlement } from '@benilar/shared';
import type { ApiClient } from '../api.js';

interface Props {
  api: ApiClient;
}

/**
 * Admin console: register customer accounts, trigger child-account discovery for
 * management accounts, and grant entitlements to individual employees.
 */
export function AdminPage({ api }: Props): JSX.Element {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [a, e] = await Promise.all([api.listAccounts(), api.listEntitlements()]);
      setAccounts(a.accounts);
      setEntitlements(e.entitlements);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const discover = useCallback(
    async (managementAccountId: string) => {
      setMessage(null);
      setError(null);
      try {
        const res = await api.discoverChildren(managementAccountId);
        setMessage(
          `Discovered ${res.discovered.length} new child account(s); ${res.alreadyKnown.length} already known.`,
        );
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [api, refresh],
  );

  return (
    <section>
      <h1>Administration</h1>
      {message && <div className="card ok">{message}</div>}
      {error && <div className="card error">{error}</div>}

      <RegisterAccountForm api={api} onDone={refresh} onError={setError} />

      <h2>Customer accounts ({accounts.length})</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Account</th>
            <th>Customer</th>
            <th>Type</th>
            <th>Parent</th>
            <th>Roles</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr key={a.accountId}>
              <td><code>{a.accountId}</code></td>
              <td>{a.customerName}</td>
              <td>{a.accountType}</td>
              <td>{a.parentAccountId ? <code>{a.parentAccountId}</code> : '—'}</td>
              <td>{a.availableRoles.join(', ')}</td>
              <td>{a.status}</td>
              <td>
                {a.accountType === 'management' && (
                  <button onClick={() => void discover(a.accountId)}>Discover children</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <GrantEntitlementForm api={api} accounts={accounts} onDone={refresh} onError={setError} />

      <h2>Entitlements ({entitlements.length})</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>User</th>
            <th>Scope</th>
            <th>Role</th>
            <th>Max session</th>
            <th>Expires</th>
          </tr>
        </thead>
        <tbody>
          {entitlements.map((e) => (
            <tr key={e.entitlementId}>
              <td>{e.principalEmail}</td>
              <td>
                {e.scope.kind === 'account'
                  ? `account ${e.scope.accountId}`
                  : `mgmt ${e.scope.managementAccountId}${e.scope.includeChildren ? ' + children' : ''}`}
              </td>
              <td>{e.roleName}</td>
              <td>{Math.round(e.maxSessionDurationSeconds / 60)} min</td>
              <td>{e.expiresAt ? new Date(e.expiresAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RegisterAccountForm({
  api,
  onDone,
  onError,
}: {
  api: ApiClient;
  onDone: () => Promise<void>;
  onError: (m: string) => void;
}): JSX.Element {
  const [accountId, setAccountId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [accountType, setAccountType] = useState<Account['accountType']>('standalone');
  const [externalId, setExternalId] = useState('');
  const [roles, setRoles] = useState('BenilarProservReadOnly,BenilarProservAdmin');

  const submit = async (ev: React.FormEvent): Promise<void> => {
    ev.preventDefault();
    try {
      await api.createAccount({
        accountId,
        customerName,
        accountType,
        externalId,
        availableRoles: roles.split(',').map((r) => r.trim()).filter(Boolean),
        homeRegion: 'eu-west-3',
      });
      setAccountId('');
      setCustomerName('');
      setExternalId('');
      await onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      <h3>Register a customer account</h3>
      <div className="row">
        <input placeholder="Account ID (12 digits)" value={accountId} onChange={(e) => setAccountId(e.target.value)} required />
        <input placeholder="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
        <select value={accountType} onChange={(e) => setAccountType(e.target.value as Account['accountType'])}>
          <option value="standalone">standalone</option>
          <option value="management">management</option>
        </select>
      </div>
      <div className="row">
        <input placeholder="External ID" value={externalId} onChange={(e) => setExternalId(e.target.value)} required />
        <input placeholder="Available roles (comma-separated)" value={roles} onChange={(e) => setRoles(e.target.value)} required />
        <button className="primary" type="submit">Register</button>
      </div>
    </form>
  );
}

function GrantEntitlementForm({
  api,
  accounts,
  onDone,
  onError,
}: {
  api: ApiClient;
  accounts: Account[];
  onDone: () => Promise<void>;
  onError: (m: string) => void;
}): JSX.Element {
  const [principalSub, setPrincipalSub] = useState('');
  const [principalEmail, setPrincipalEmail] = useState('');
  const [targetAccountId, setTargetAccountId] = useState('');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [roleName, setRoleName] = useState('BenilarProservReadOnly');
  const [duration, setDuration] = useState(3600);

  const selected = accounts.find((a) => a.accountId === targetAccountId);

  const submit = async (ev: React.FormEvent): Promise<void> => {
    ev.preventDefault();
    if (!selected) {
      onError('Select a target account');
      return;
    }
    try {
      await api.createEntitlement({
        principalSub,
        principalEmail,
        roleName,
        maxSessionDurationSeconds: duration,
        scope:
          selected.accountType === 'management'
            ? { kind: 'management', managementAccountId: selected.accountId, includeChildren }
            : { kind: 'account', accountId: selected.accountId },
      });
      setPrincipalSub('');
      setPrincipalEmail('');
      await onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      <h3>Grant an entitlement</h3>
      <div className="row">
        <input placeholder="Cognito sub" value={principalSub} onChange={(e) => setPrincipalSub(e.target.value)} required />
        <input placeholder="Employee email" value={principalEmail} onChange={(e) => setPrincipalEmail(e.target.value)} required />
      </div>
      <div className="row">
        <select value={targetAccountId} onChange={(e) => setTargetAccountId(e.target.value)} required>
          <option value="">Select account…</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.customerName} ({a.accountId}) — {a.accountType}
            </option>
          ))}
        </select>
        <select value={roleName} onChange={(e) => setRoleName(e.target.value)}>
          {(selected?.availableRoles ?? ['BenilarProservReadOnly', 'BenilarProservAdmin']).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>
      <div className="row">
        {selected?.accountType === 'management' && (
          <label className="checkbox">
            <input type="checkbox" checked={includeChildren} onChange={(e) => setIncludeChildren(e.target.checked)} />
            Include discovered child accounts
          </label>
        )}
        <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
          <option value={900}>15 min</option>
          <option value={1800}>30 min</option>
          <option value={3600}>60 min</option>
        </select>
        <button className="primary" type="submit">Grant</button>
      </div>
    </form>
  );
}
